import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import { SeoProject } from '@/types/api/seo';

type Props = {
  projects: SeoProject[];
  isLoading: boolean;
  onNewProject: () => void;
};

const formatDomain = (project: SeoProject): string =>
  project.domain || project.siteUrl?.replace('sc-domain:', '').replace(/^https?:\/\//, '').replace(/\/$/, '') || 'No domain';

const SEOxProjectListComponent = function ({
  projects,
  isLoading,
  onNewProject,
}: Props): JSX.Element {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#054AD9' }} />
      </Box>
    );
  }

  return (
    <Box sx={{}}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrendingUpOutlinedIcon sx={{ fontSize: 26, color: '#054AD9' }} />
          <Typography variant="h5" fontWeight={700}>SEOx Projects</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewProject}
          sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
        >
          New Project
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each project tracks one website. Select a project to view its SEO data.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
        {projects.map((project) => (
          <Link
            key={project.id}
            to={`/seox/projects/${project.shortId}`}
            style={{ textDecoration: 'none' }}
          >
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 3,
                backgroundColor: '#fff',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#054AD9',
                  boxShadow: '0 4px 12px rgba(5,74,217,0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LanguageIcon sx={{ fontSize: 20, color: '#054AD9' }} />
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  {project.name}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {formatDomain(project)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {project.siteUrl && (
                  <Chip label="GSC" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                )}
                {project.ga4PropertyId && (
                  <Chip label="GA4" size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                )}
              </Box>
            </Box>
          </Link>
        ))}
      </Box>
    </Box>
  );
};

export default SEOxProjectListComponent;
